import { useEffect, useRef, useState } from 'preact/hooks'
import { AlertTriangle, Captions, FileText, Info, Loader2, Play, Upload } from 'lucide-preact'
import type { WebViewDto } from '@/api/webs'
import { webinarRoomApi } from '@/api/webinarRoom'
import type { WebinarRoomRecordingViewDto } from '@/api/webinarRoom'
import { Select } from '@/components/common/Select'
import { useToast } from '@/hooks/useToast'
import { useWorkspaceCapabilities } from '@/hooks/useWorkspaceCapabilities'
import { roomHasReplay } from '@/utils/webinarRoomSetup'
import type { SubtitleLanguageCode } from '@/webinarRoom/limits'
import { LOCK_TITLE } from './roomRecording'
import { BUTTON, BUTTON_GHOST, NOTE, STRIP_ERROR } from './roomStyles'
import { SUBTITLES_ACCEPT, SUBTITLE_LANGUAGE_OPTIONS, refusalLine, refusalReason, subtitleFileProblem, subtitlesAddedByLine, subtitlesFacts } from './roomSubtitles'

interface RoomSubtitlesSectionProps {
    web: WebViewDto
    /** The recording in use; subtitles are timed to it, so they wait for it to be ready. */
    recording: WebinarRoomRecordingViewDto | null
    isSignedOff: boolean
    previewOpen: boolean
    onPreview: () => void
    /** Rereads the web, after every write here. */
    reload: () => Promise<void>
}

/**
 * The Recording card's Subtitles section (ORB-1132): an SRT or WebVTT file for the ready recording, in one spoken
 * language. Optional, so it holds nothing up. A file the API refuses says why in the red strip, under its own name.
 * Locked once signed off, like the recording.
 */
export function RoomSubtitlesSection({ web, recording, isSignedOff, previewOpen, onPreview, reload }: RoomSubtitlesSectionProps) {
    const { canEdit } = useWorkspaceCapabilities()
    const { showErrorUnless422 } = useToast()
    const [languageCode, setLanguageCode] = useState<SubtitleLanguageCode>('en')
    const [refusal, setRefusal] = useState<string | null>(null)
    const [busy, setBusy] = useState(false)
    const fileInput = useRef<HTMLInputElement>(null)
    // Add sends the language picked here; Replace keeps the language the subtitles are in.
    const uploadLanguage = useRef<string>('en')

    const ready = recording?.State === 'Ready' ? recording : null
    const subtitles = ready?.Subtitles ?? null
    const locked = isSignedOff || !canEdit
    const lockTitle = isSignedOff ? LOCK_TITLE : undefined
    const now = new Date()

    // A refusal is about a file chosen for this recording; another recording has no such file.
    useEffect(() => setRefusal(null), [recording?.Id])

    const chooseFile = (language: string) => {
        uploadLanguage.current = language
        fileInput.current?.click()
    }

    const upload = async (file: File) => {
        if (!ready) return
        const problem = subtitleFileProblem(file)
        if (problem) { setRefusal(refusalLine(file.name, problem)); return }
        setRefusal(null)
        setBusy(true)
        try {
            const res = await webinarRoomApi.uploadRecordingSubtitles(web.WorkspaceId, web.Id, ready.Id, file, uploadLanguage.current)
            if (res.status === 422) setRefusal(refusalLine(file.name, refusalReason(res)))
            else if (res.error) showErrorUnless422('Not added', res, 'The subtitles could not be added.')
        } finally {
            await reload()
            setBusy(false)
        }
    }

    const remove = async () => {
        if (!ready) return
        setBusy(true)
        try {
            const res = await webinarRoomApi.removeRecordingSubtitles(web.WorkspaceId, web.Id, ready.Id)
            if (res.error) showErrorUnless422('Not removed', res, 'The subtitles could not be removed.')
        } finally {
            await reload()
            setBusy(false)
        }
    }

    const body = (() => {
        if (!ready) {
            return <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">Add subtitles once the recording is ready. They’re checked against its length.</p>
        }
        if (subtitles) {
            const addedBy = subtitlesAddedByLine(subtitles, now)
            const where = roomHasReplay(web.OnlineEvent!) ? 'live and on the replay' : 'live'
            return (
                <>
                    <div className="mt-3 grid grid-cols-[44px_minmax(0,1fr)] gap-3.5 items-start">
                        <span className="w-11 h-11 rounded-lg grid place-items-center bg-slate-50 dark:bg-slate-900/40 border border-slate-200 dark:border-slate-700 text-slate-400">
                            <FileText className="w-5 h-5" />
                        </span>
                        <div className="min-w-0">
                            <p className="font-medium text-slate-900 dark:text-white truncate">{subtitles.FileName}</p>
                            <p className="text-sm tabular-nums text-slate-500 dark:text-slate-400">{subtitlesFacts(subtitles)}</p>
                            {addedBy && <p className="text-sm text-slate-500 dark:text-slate-400">{addedBy}</p>}
                            {canEdit && (
                                <div className="mt-3.5 flex flex-wrap items-center gap-2">
                                    {ready.HasPlayback && !previewOpen && (
                                        <button type="button" className={BUTTON} onClick={onPreview}><Play className="w-3 h-3" />Preview with subtitles</button>
                                    )}
                                    <button type="button" className={BUTTON} disabled={locked || busy} title={lockTitle} onClick={() => chooseFile(subtitles.LanguageCode)}>
                                        <Upload className="w-3.5 h-3.5" />Replace
                                    </button>
                                    <button type="button" className={BUTTON_GHOST} disabled={locked || busy} title={lockTitle} onClick={() => void remove()}>Remove</button>
                                    {busy && <Loader2 className="w-4 h-4 animate-spin text-slate-400" aria-label="Saving the subtitles" />}
                                </div>
                            )}
                        </div>
                    </div>
                    {refusal && <Refusal text={refusal} />}
                    <p className={NOTE}>
                        <Info className="w-3.5 h-3.5 mt-0.5 flex-none text-slate-400" />
                        <span>Viewers turn them on with CC, {where}. They’re timed to this recording, so replacing the recording removes them.</span>
                    </p>
                    {isSignedOff && <p className="mt-2.5 text-sm text-slate-500 dark:text-slate-400">Signed off. Reopen the web to change the recording or its subtitles.</p>}
                </>
            )
        }
        if (!canEdit) return <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">No subtitles added.</p>
        return (
            <>
                <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">Add an SRT or WebVTT file and viewers get a CC button, live{roomHasReplay(web.OnlineEvent!) ? ' and on the replay' : ''}.</p>
                {refusal && <Refusal text={refusal} />}
                <div className="mt-3.5 flex flex-wrap items-end gap-2.5">
                    <Select
                        name="room-subtitles-language"
                        label="Spoken language"
                        containerClassName="min-w-[180px]"
                        options={SUBTITLE_LANGUAGE_OPTIONS}
                        value={languageCode}
                        disabled={locked || busy}
                        title={lockTitle}
                        onChange={(event: Event) => setLanguageCode((event.currentTarget as HTMLSelectElement).value as SubtitleLanguageCode)}
                    />
                    <button type="button" className={BUTTON} disabled={locked || busy} title={lockTitle} onClick={() => chooseFile(languageCode)}>
                        <Upload className="w-3.5 h-3.5" />Add subtitles
                    </button>
                    {busy && <Loader2 className="w-4 h-4 mb-2 animate-spin text-slate-400" aria-label="Adding the subtitles" />}
                </div>
                <p className={`${NOTE} mt-3`}>
                    <Info className="w-3.5 h-3.5 mt-0.5 flex-none text-slate-400" />
                    <span>From Descript: Export, then Subtitles, as SRT or VTT. Speaker names you include there show as typed.</span>
                </p>
            </>
        )
    })()

    return (
        <div className="mt-6 pt-5 border-t border-slate-200 dark:border-slate-700" aria-labelledby="room-subtitles-title" role="group">
            <div className="flex items-baseline justify-between gap-3">
                <h3 id="room-subtitles-title" className="text-[15px] font-semibold text-slate-900 dark:text-white flex items-center gap-2 self-center">
                    <Captions className="w-[18px] h-[18px] text-primary-600" />Subtitles
                </h3>
                <span className="text-[13px] text-slate-500 dark:text-slate-400">Optional</span>
            </div>
            {body}
            {/* The buttons are the control; the picker stays hidden, as the drop zones' do. */}
            <input
                ref={fileInput}
                type="file"
                accept={SUBTITLES_ACCEPT}
                className="hidden"
                aria-hidden="true"
                tabIndex={-1}
                onChange={event => {
                    const target = event.currentTarget
                    const file = target.files?.[0]
                    target.value = ''
                    if (file) void upload(file)
                }}
            />
        </div>
    )
}

function Refusal({ text }: { text: string }) {
    return (
        <div role="alert" className={STRIP_ERROR}>
            <AlertTriangle className="w-4 h-4 mt-0.5 flex-none text-red-600 dark:text-red-400" />
            <p>{text}</p>
        </div>
    )
}
